"use client";

import { c as _c } from "react/compiler-runtime";
import type { FC } from "react";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { ApiWaveOutcome } from "@/generated/models/ApiWaveOutcome";
import type { WaveOutcomeDistributionState } from "@/types/waves.types";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import { SparklesIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
interface WaveRepOutcomeProps {
  readonly outcome: ApiWaveOutcome;
  readonly distribution: WaveOutcomeDistributionState;
}
const DEFAULT_AMOUNTS_TO_SHOW = 3;
export const WaveRepOutcome: FC<WaveRepOutcomeProps> = t0 => {
  const $ = _c(57);
  const {
    outcome,
    distribution
  } = t0;
  const [isOpen, setIsOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const {
    items,
    totalCount,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isLoading,
    isError,
    errorMessage
  } = distribution;
  const winnersCount = totalCount;
  let t1;
  if ($[0] !== items || $[1] !== showAll) {
    t1 = showAll ? items : items.slice(0, DEFAULT_AMOUNTS_TO_SHOW);
    $[0] = items;
    $[1] = showAll;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const visibleItems = t1;
  const hiddenLocalCount = Math.max(items.length - DEFAULT_AMOUNTS_TO_SHOW, 0);
  const remainingFromServer = Math.max(totalCount - items.length, 0);
  const remainingCount = showAll ? remainingFromServer : hiddenLocalCount + remainingFromServer;
  const shouldShowMore = hasNextPage || !showAll && items.length > DEFAULT_AMOUNTS_TO_SHOW;
  let t2;
  if ($[3] !== fetchNextPage || $[4] !== hasNextPage || $[5] !== isFetchingNextPage || $[6] !== showAll) {
    t2 = () => {
      if (!showAll) {
        setShowAll(true);
      }
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    };
    $[3] = fetchNextPage;
    $[4] = hasNextPage;
    $[5] = isFetchingNextPage;
    $[6] = showAll;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const onViewMore = t2;
  let t3;
  if ($[8] !== isOpen) {
    t3 = () => setIsOpen(!isOpen);
    $[8] = isOpen;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="tw-flex tw-items-center tw-justify-center tw-size-10 tw-rounded-lg tw-bg-[#C3B5D9]/10"><SparklesIcon className="tw-size-5 tw-text-[#C3B5D9]" /></div>;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="tw-text-sm tw-font-medium tw-text-iron-200">Rep</div>;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== isLoading || $[13] !== winnersCount) {
    t6 = <div className="tw-flex tw-items-center tw-gap-3">{t4}<div className="tw-text-left">{t5}<div className="tw-text-xs tw-text-iron-500">{isLoading ? <span className="tw-animate-pulse tw-bg-iron-800 tw-h-3 tw-w-16 tw-rounded tw-inline-block" /> : <>{formatNumberWithCommas(winnersCount)}{" "}{winnersCount === 1 ? "winner" : "winners"}</>}</div></div></div>;
    $[12] = isLoading;
    $[13] = winnersCount;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const t7 = outcome.amount ?? 0;
  let t8;
  if ($[15] !== t7) {
    t8 = formatNumberWithCommas(t7);
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t8) {
    t9 = <div className="tw-text-sm tw-font-semibold tw-text-[#C3B5D9]">{t8}</div>;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="tw-text-xs tw-text-iron-500">total pool</div>;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t9) {
    t11 = <div className="tw-text-right">{t9}{t10}</div>;
    $[20] = t9;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const t12 = isOpen ? 180 : 0;
  let t13;
  if ($[22] !== t12) {
    t13 = {
      rotate: t12
    };
    $[22] = t12;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  let t15;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      duration: 0.2
    };
    t15 = <ChevronDownIcon className="tw-size-4 tw-text-iron-500" />;
    $[24] = t14;
    $[25] = t15;
  } else {
    t14 = $[24];
    t15 = $[25];
  }
  let t16;
  if ($[26] !== t13) {
    t16 = <motion.div animate={t13} transition={t14}>{t15}</motion.div>;
    $[26] = t13;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== t11 || $[29] !== t16) {
    t17 = <div className="tw-flex tw-items-center tw-gap-4">{t11}{t16}</div>;
    $[28] = t11;
    $[29] = t16;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== t17 || $[32] !== t6) {
    t18 = <div className="tw-flex tw-items-center tw-justify-between">{t6}{t17}</div>;
    $[31] = t17;
    $[32] = t6;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== outcome.rep_category) {
    t19 = outcome.rep_category && <div className="tw-mt-3 tw-pt-3 tw-border-t tw-border-iron-800/50 tw-border-solid tw-border-x-0 tw-border-b-0"><div className="tw-flex tw-items-center tw-justify-between"><span className="tw-text-xs tw-text-iron-500">Category</span><span className="tw-text-xs tw-text-iron-300">{outcome.rep_category}</span></div></div>;
    $[34] = outcome.rep_category;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== isOpen || $[37] !== t18 || $[38] !== t19 || $[39] !== t3) {
    t20 = <button type="button" aria-expanded={isOpen} onClick={t3} className="tw-w-full tw-border-0 tw-px-4 tw-py-4 tw-bg-transparent tw-cursor-pointer">{t18}{t19}</button>;
    $[36] = isOpen;
    $[37] = t18;
    $[38] = t19;
    $[39] = t3;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== errorMessage || $[42] !== isError || $[43] !== isFetchingNextPage || $[44] !== isLoading || $[45] !== isOpen || $[46] !== items.length || $[47] !== onViewMore || $[48] !== remainingCount || $[49] !== shouldShowMore || $[50] !== visibleItems) {
    t21 = isOpen && <motion.div initial={{
      height: 0,
      opacity: 0
    }} animate={{
      height: "auto",
      opacity: 1
    }} exit={{
      height: 0,
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="tw-overflow-hidden"><div className="tw-border-t tw-border-iron-800/50 tw-border-solid tw-border-x-0 tw-border-b-0">{visibleItems.map(_temp)}{isLoading && <div className="tw-px-4 tw-py-3 tw-text-center tw-text-sm tw-text-iron-500">Loading winners...</div>}{isError && <div className="tw-px-4 tw-py-3 tw-text-center tw-text-sm tw-text-red-400">{errorMessage || "Failed to load winners"}</div>}{!isLoading && !isError && items.length === 0 && <div className="tw-px-4 tw-py-3 tw-text-center tw-text-sm tw-text-iron-500">No winners yet</div>}{shouldShowMore && <button className="tw-border-0 tw-w-full tw-px-4 tw-py-3 tw-text-sm tw-text-iron-500 desktop-hover:hover:tw-text-iron-300 tw-bg-transparent tw-transition-colors tw-cursor-pointer" onClick={onViewMore} disabled={isFetchingNextPage}>{isFetchingNextPage ? "Loading..." : `View ${remainingCount} more`}</button>}</div></motion.div>;
    $[41] = errorMessage;
    $[42] = isError;
    $[43] = isFetchingNextPage;
    $[44] = isLoading;
    $[45] = isOpen;
    $[46] = items.length;
    $[47] = onViewMore;
    $[48] = remainingCount;
    $[49] = shouldShowMore;
    $[50] = visibleItems;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  let t22;
  if ($[52] !== t21) {
    t22 = <AnimatePresence initial={false}>{t21}</AnimatePresence>;
    $[52] = t21;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  let t23;
  if ($[54] !== t20 || $[55] !== t22) {
    t23 = <div className="tw-rounded-xl tw-bg-iron-950 tw-ring-1 tw-ring-white/5 tw-transition-all tw-duration-300 desktop-hover:hover:tw-ring-white/10">{t20}{t22}</div>;
    $[54] = t20;
    $[55] = t22;
    $[56] = t23;
  } else {
    t23 = $[56];
  }
  return t23;
};
function _temp(item, i) {
  return <div key={`wave-rep-outcome-${item.index}`} className="tw-px-4 tw-py-3 tw-flex tw-items-center tw-justify-between tw-transition-colors desktop-hover:hover:tw-bg-white/[0.02]"><div className="tw-flex tw-items-center tw-gap-3"><span className="tw-flex tw-items-center tw-justify-center tw-size-6 tw-rounded-md tw-bg-iron-900 tw-text-iron-400 tw-text-xs tw-font-medium">{i + 1}</span><span className="tw-text-sm tw-text-iron-200">{formatNumberWithCommas(item.amount ?? 0)} Rep</span></div></div>;
}